import type { Bundle, Reference, Task } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { ExtendedClientApplication } from '@seen/core/models/clientApplication'
import { isOfType } from '@seen/core/utils'

import { cn, DotSeparator, TypographyDetail } from '@seen/ui'
import { type JSX, type ReactNode, useEffect } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Logo } from '#src/components/core/Logo.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { IconRelatedArrow } from '#src/components/icons/IconRelatedArrow.tsx'

interface ActivityEntryProps {
  // Line above the entry, aligned with the author name.
  label?: ReactNode
  author?: Reference
  time?: Date | string
  timeFormat?: 'date' | 'datetime'
  // Extra line shown in the time tooltip, e.g. the authored date when the
  // displayed time is a backdated clinical start date.
  timeTooltipDetail?: string
  children?: ReactNode
  action?: ReactNode
  // The queued-edits review record this change originated from, when this entry
  // is the version that applied it — a QueuedEditsReviewTask (async/inbox) or
  // the proposal Bundle (inline ai-chat, no Task). Renders an inline "· as part
  // of …" backlink after the action: a hover-card for a Task, a static label
  // for a Bundle (a bare Bundle has no inbox page). Generic across feeds —
  // derive it with `introducedQueuedEditsOriginTask(curr, prev)`.
  originTask?: Reference<Task | Bundle>
  nestedEvents?: {
    author?: Reference
    time?: Date | string
    timeFormat?: 'date' | 'datetime'
    action: ReactNode
    children?: ReactNode
  }[]
  className?: string
}

export function ActivityEntry({
  label,
  author,
  time,
  timeFormat = 'datetime',
  timeTooltipDetail,
  children,
  action,
  originTask,
  nestedEvents,
  className,
}: ActivityEntryProps): JSX.Element {
  useEffect(() => {
    if (
      author &&
      !isOfType(author, 'Practitioner') &&
      !isOfType(author, 'ClientApplication') &&
      !isOfType(author, 'Organization') &&
      !isOfType(author, 'Bot')
    ) {
      // TODO: Add support for other author types.
      console.warn('Unknown author type', author)
    }
  }, [author])

  return (
    <div
      className={cn(
        'group flex flex-col gap-1.5 overflow-hidden py-1',
        className
      )}
    >
      {label && <div className="ml-9.5">{label}</div>}
      <div className="flex items-center gap-2">
        <div
          className={cn([
            'ml-2.5',
            '-my-1.5 z-10 py-1.5', // Add visual margin between the avatars and the vertical line from ActivityList.tsx. Design from linear.app.
            'group-last:mb-[-100rem] group-last:pb-[100rem]', // Prevent the vertical line from ActivityList.tsx from emanating below the last entry.
          ])}
        >
          {author ? (
            <Avatar profile={author} size="icon" />
          ) : (
            <div className="flex size-5 items-center justify-center rounded-full bg-muted">
              <Logo size={12} />
            </div>
          )}
        </div>
        <TypographyDetail>
          {author && isOfType(author, 'Practitioner') && (
            <HoverPractitioner practitioner={author} />
          )}
          {author &&
            (isOfType(author, 'ClientApplication') ||
              isOfType(author, 'Bot')) &&
            ExtendedClientApplication.companyName}
          {author && isOfType(author, 'Organization') && (
            <>{referenceDisplay(author)}</>
          )}
          {author &&
            !isOfType(author, 'Practitioner') &&
            !isOfType(author, 'ClientApplication') &&
            !isOfType(author, 'Organization') &&
            !isOfType(author, 'Bot') &&
            'Unknown'}
          {!author && 'System'}
          {time && (
            <>
              {' '}
              <span className="font-normal text-foreground-secondary">
                {action && <>{action} </>}
                {originTask && (
                  <span className="inline-flex items-center gap-1 align-middle">
                    <span className="text-muted-foreground">as part of</span>
                    {isOfType(originTask, 'Task') ? (
                      <HoverTask task={originTask} />
                    ) : (
                      <span>
                        {referenceDisplay(originTask) ??
                          'an AI-proposed change'}
                      </span>
                    )}
                  </span>
                )}
                <DotSeparator />
                <HoverDate
                  date={time}
                  format={timeFormat}
                  tooltipDetail={timeTooltipDetail}
                />
              </span>
            </>
          )}
        </TypographyDetail>
      </div>
      {children && <div className="ml-9.5">{children}</div>}
      {nestedEvents && nestedEvents.length > 0 && (
        <div
          className="ml-9.5 flex flex-col gap-1"
          data-testid="activity-nested-events"
        >
          {nestedEvents.map((nestedEvent, idx) => (
            <div key={idx} className="flex items-start gap-1 text-xs">
              <IconRelatedArrow className="size-4" />
              <div className="flex-1">
                <TypographyDetail>
                  {nestedEvent.author &&
                    isOfType(nestedEvent.author, 'Practitioner') && (
                      <HoverPractitioner practitioner={nestedEvent.author} />
                    )}
                  {nestedEvent.author &&
                    (isOfType(nestedEvent.author, 'ClientApplication') ||
                      isOfType(nestedEvent.author, 'Bot')) &&
                    ExtendedClientApplication.companyName}
                  {nestedEvent.author &&
                    isOfType(nestedEvent.author, 'Organization') && (
                      <>{referenceDisplay(nestedEvent.author)}</>
                    )}
                  {nestedEvent.author &&
                    !isOfType(nestedEvent.author, 'Practitioner') &&
                    !isOfType(nestedEvent.author, 'ClientApplication') &&
                    !isOfType(nestedEvent.author, 'Organization') &&
                    !isOfType(nestedEvent.author, 'Bot') &&
                    'Unknown'}
                  {nestedEvent.time && (
                    <>
                      {' '}
                      <span className="font-normal text-foreground-secondary">
                        {nestedEvent.action && <>{nestedEvent.action} </>}
                        <DotSeparator />
                        <HoverDate
                          date={nestedEvent.time}
                          format={nestedEvent.timeFormat ?? 'datetime'}
                        />
                      </span>
                    </>
                  )}
                </TypographyDetail>
                {nestedEvent.children && (
                  <div className="mt-1">{nestedEvent.children}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
